<template>
  <div class="kabel-teaser__container">
    <div class="kabel-teaser__img">
      <CdnImage
        alt="Anker kabel"
        :src="variables.mobile.bgImage"
        :src-sm="variables.tablet.bgImage"
        :src-md="variables.desktop.bgImage"
        lazy
        class="kabel-teaser__img-bg"
      />
      <Heading
        tag="h2"
        size="md"
        font-weight="bold"
        class="kabel-teaser__heading"
      >
        {{ title }}
      </Heading>
      <Paragraph class="kabel-teaser__paragraph">
        {{ subtitle }}
      </Paragraph>
      <CdnImage
        class="kabel-teaser__material"
        :src="currentScreenSizeVariables.materialImage"
        alt="Anker Eco Material"
        lazy
      />
      <CdnImage
        class="kabel-teaser__logo"
        :src="'/content/content-images/landingpages/anker/kabel-teaser/621d09e6b58cdcfd97558ec01154a5c5/icon-anker-eco-product@2x.png'"
        alt="Anker Eco Product"
        lazy
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import CdnImage from '~/components/cdn-image.vue';
import { Heading, Paragraph } from '@i22/td-longtail-vue-components';
import { ref, computed, defineComponent, watch, onMounted } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import { screenSize } from '~/composables/use-screen-size';
import type { gsap } from '~/lib/gsap';
import useScrollAnimation from './animations/scroll-animation';

defineComponent({ name: 'KabelTeaser' });

const { createTimeline } = useScrollAnimation();
const title = 'Kabel aus biobasierten Materialien ';
const subtitle =
  'Anker ist das erste Unternehmen, das biobasierte Materialien wie Zuckerrohr und Mais verwendet, um das Äußere von Ladekabeln zu konstruieren.';
const tl: Ref<gsap.core.Timeline | null> = ref(null);

interface ScreenSize {
  bgImage: string;
  materialImage: string;
  animation: {
    imgTo: gsap.TweenVars;
    logoTo?: gsap.TweenVars;
    paragraphTo: gsap.TweenVars;
    headingTo?: gsap.TweenVars;
    materialTo?: gsap.TweenVars;
  };
}

const variables: Record<string, ScreenSize> = {
  mobile: {
    bgImage:
      '/content/content-images/landingpages/anker/kabel-teaser/c1f3de50665c19117c59dd8f2d094441/kabel-teaser-visual-s@2x.jpg',
    materialImage:
      '/content/content-images/landingpages/anker/kabel-teaser/d708054c25f8b72ff0240c1a508187a4/kabel-material.png',
    animation: {
      imgTo: {
        height: 390,
        duration: 0.5,
        ease: 'power1.out',
      },
      logoTo: {
        opacity: 1,
        top: '36px',
        duration: 0.5,
        delay: 0.1,
        ease: 'power1.out',
      },
      paragraphTo: {
        opacity: 1,
        duration: 0.5,
        delay: 0.1,
      },
      materialTo: {
        opacity: 0,
        duration: 0.5,
      },
    },
  },
  tablet: {
    bgImage:
      '/content/content-images/landingpages/anker/kabel-teaser/bf19d874e957aac6f9debc7c97521b0a/kabel-teaser-visual-m@2x.jpg',
    materialImage:
      '/content/content-images/landingpages/anker/kabel-teaser/d708054c25f8b72ff0240c1a508187a4/kabel-material.png',
    animation: {
      imgTo: {
        height: 390,
        duration: 0.5,
        ease: 'power1.out',
      },
      logoTo: {
        opacity: 1,
        top: 'calc(50% - 60px)',
        duration: 0.5,
        delay: 0.1,
      },
      paragraphTo: {
        opacity: 1,
        duration: 0.5,
        delay: 0.1,
        marginTop: '12px',
      },
      headingTo: {
        marginTop: '24px',
        duration: 0.5,
      },
      materialTo: {
        opacity: 1,
        duration: 0.5,
        ease: 'power1.out',
      },
    },
  },
  desktop: {
    bgImage:
      '/content/content-images/landingpages/anker/kabel-teaser/afe5c0cdb9ca81fc95fd9b8e63116d71/kabel-teaser-visual@2x.jpg',
    materialImage:
      '/content/content-images/landingpages/anker/kabel-teaser/0a789cfe1844fbcc90ac2e7fb1d8f572/kabel-material@2x.png',
    animation: {
      imgTo: {
        height: 390,
        duration: 0.5,
        ease: 'power1.out',
      },
      logoTo: {
        opacity: 1,
        duration: 0.5,
        top: '65px',
        ease: 'power1.out',
      },
      paragraphTo: {
        opacity: 1,
        duration: 0.5,
        marginLeft: '0px',
        ease: 'back',
      },
      headingTo: {
        marginTop: '48px',
        duration: 0.5,
      },
      materialTo: {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        ease: 'power1.out',
      },
    },
  },
};
variables.largeDesktop = variables.desktop;

const currentScreenSizeVariables: ComputedRef<ScreenSize> = computed(() =>
  variables[screenSize.value] ? variables[screenSize.value] : variables.tablet
);

function buildTimeline() {
  tl.value = createTimeline({
    scrollTrigger: {
      invalidateOnRefresh: true,
      trigger: '.kabel-teaser__container',
      start: `top center`,
      end: `bottom bottom`,
      toggleActions: 'play none play reverse',
    },
  });
  tl.value.to('.kabel-teaser__img', currentScreenSizeVariables.value.animation.imgTo);
  if (currentScreenSizeVariables.value.animation.logoTo) {
    tl.value.to('.kabel-teaser__logo', currentScreenSizeVariables.value.animation.logoTo, '<');
  }
  if (currentScreenSizeVariables.value.animation.headingTo) {
    tl.value.to(
      '.kabel-teaser__heading',
      currentScreenSizeVariables.value.animation.headingTo,
      '<'
    );
  }
  if (currentScreenSizeVariables.value.animation.materialTo) {
    tl.value.to(
      '.kabel-teaser__material',
      currentScreenSizeVariables.value.animation.materialTo,
      '<'
    );
  }
  tl.value.to(
    '.kabel-teaser__paragraph',
    currentScreenSizeVariables.value.animation.paragraphTo,
    '<'
  );
  tl.value.pause();
}

watch(currentScreenSizeVariables, () => {
  buildTimeline();
});

onMounted(() => {
  buildTimeline();
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
.kabel-teaser {
  &__container {
    height: 390px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &__img {
    position: relative;
    width: 100%;
    height: 160px;
    overflow: hidden;
    padding-left: $grid-base;
    padding-top: $grid-base * 3;
    @include mq($mq-sm) {
      padding-left: 2 * $grid-base;
    }
  }

  &__img-bg {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
    z-index: -1;

    :deep(img) {
      min-width: 100%;
      min-height: 100%;
      width: 100%;
      object-fit: cover;
      aspect-ratio: 64 / 39;

      @include mq($mq-sm) {
        aspect-ratio: 512 / 175;
      }

      @include mq($mq-md) {
        aspect-ratio: 640 / 161;
      }
    }
  }

  &__logo {
    width: 70px;
    position: absolute;
    right: 3 * $grid-base;
    top: 72px;
    opacity: 0;
    @include mq($mq-sm) {
      opacity: 1;
      width: 90px;
      right: 100px;
      top: calc(50% - 60px);
    }
    @include mq($mq-md) {
      top: 30px;
    }
    :deep(img) {
      width: 100%;
      aspect-ratio: 108 / 115;
    }
  }
  &__material {
    position: absolute;
    width: 230px;
    left: 35%;
    top: -50px;
    opacity: 0;

    @include mq(false, $mq-sm - 1) {
      display: none;
    }

    @include mq($mq-md) {
      left: 35%;
      width: 300px;
      transform: scale(0.5);
    }

    :deep(img) {
      width: 100%;
      aspect-ratio: 1 / 1;
    }
  }
  &__heading {
    @include font-size(36px, 36px);
    max-width: 200px;
    margin-bottom: $grid-base;
    @include mq($mq-sm) {
      max-width: 300px;
    }
    @include mq($mq-md) {
      max-width: 420px;
    }
  }
  &__paragraph {
    max-width: 250px;
    opacity: 0;
    @include mq($mq-sm) {
      max-width: 300px;
    }
    @include mq($mq-md) {
      max-width: 360px;
      margin-left: 20%;
      margin-top: 24px;
    }
  }
}
</style>
